import { nonEmptyTrimmedStringFromUnknown } from "./shared.js";
import { agentProfileIdSchema } from "./profile.js";
import { localAgentReasoningStateSchema } from "../http/local-agent-reasoning.js";
import * as z$1 from "zod/v4";
//#region src/schemas/protocol/agent-stream.ts
const AGENT_STREAM_SNAPSHOT_MAX_CHARS = 32 * 1024;
const clearSpaceReasoningReplaySchema = z$1.strictObject({
	spaceId: z$1.string().trim().min(1),
	profileId: agentProfileIdSchema
});
const sequenceSchema = z$1.number().int().positive();
const timestampSchema = z$1.number().int().nonnegative();
const textSnapshotSchema = z$1.string().min(1).max(AGENT_STREAM_SNAPSHOT_MAX_CHARS);
const toolIdentifierSchema = z$1.string().trim().min(1).max(160);
const HOSTNAME_PATTERN = /^(?:[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?)(?:\.(?:[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?))*$/iu;
const IPV6_HOSTNAME_PATTERN = /^\[[\da-f:.]+\]$/iu;
const REPOSITORY_LABEL_PATTERN = /^[a-z\d](?:[a-z\d_.-]*[a-z\d])?\/[a-z\d](?:[a-z\d_.-]*[a-z\d])?$/iu;
const agentWorkSubjectLabelSchema = z$1.string().trim().min(1).max(120).refine((label) => !hasControlCharacter(label), { message: "subject label must not contain control characters" });
function hasControlCharacter(value) {
	for (const character of value) {
		const codePoint = character.codePointAt(0);
		if (codePoint !== void 0 && (codePoint <= 31 || codePoint >= 127 && codePoint <= 159)) return true;
	}
	return false;
}
const agentWorkActivityKeySchema = z$1.enum([
	"agent.activity.read",
	"agent_profile.configure",
	"agent_profile.connect",
	"agent_profile.connections.read",
	"agent_profile.create",
	"agent_profile.enable",
	"agent_profile.readiness.check",
	"agent_profile.settings.read",
	"agent_profile.settings.update",
	"build.run",
	"center_agent.activity.read",
	"center_agent.attention.request",
	"center_agent.task.create",
	"connected_app.action.execute",
	"connected_app.actions.read",
	"connected_apps.read",
	"computer.local_agent.installation.select",
	"computer.local_agent.installations.read",
	"computer.openmeld_tools.settings.update",
	"computer.openmeld_tools.update",
	"dependencies.install",
	"dispatch.history.read",
	"documentation.read",
	"documentation.update",
	"environment.check",
	"execution.recovery.conversation.resume",
	"execution.recovery.inspect",
	"execution.recovery.result.replay",
	"execution.recovery.retry_as_new_execution",
	"feedback.create",
	"feedback.read",
	"feedback.update",
	"github.account.read",
	"github.branches.read",
	"github.checks.read",
	"github.code.search",
	"github.commits.compare",
	"github.commits.read",
	"github.commits.search",
	"github.directory.read",
	"github.file.delete",
	"github.file.read",
	"github.file.update",
	"github.issue.assignees.update",
	"github.issue.comment",
	"github.issue.create",
	"github.issue.labels.update",
	"github.issue.update",
	"github.issues.read",
	"github.issues.search",
	"github.pull_request.branch.update",
	"github.pull_request.comment",
	"github.pull_request.create",
	"github.pull_request.merge",
	"github.pull_request.review",
	"github.pull_request.reviewers.update",
	"github.pull_request.update",
	"github.pull_requests.read",
	"github.ref.create",
	"github.releases.read",
	"github.repositories.read",
	"github.repository.read",
	"github.workflow.dispatch",
	"github.workflow.rerun",
	"github.workflows.read",
	"image.generate",
	"image.inspect",
	"navigation.prepare",
	"organization.document.draft",
	"organization.documents.manage",
	"organization.documents.read",
	"organization.invite.create",
	"organization.invite.read",
	"organization.member_computers.read",
	"organization.member_research.start",
	"organization.member_service_status.read",
	"organization.members.read",
	"organization.memory.update",
	"organization.settings.update",
	"organization.skills.install",
	"organization.skills.read",
	"project.create",
	"project.default.set",
	"project.remove",
	"project.remove.prepare",
	"project.update",
	"projects.read",
	"quality.check",
	"repository.check",
	"repository.clone",
	"repository.inspect",
	"routine.create",
	"routine.delete",
	"routine.pause",
	"routine.resume",
	"routine.update",
	"routines.read",
	"slack.channel.history.read",
	"slack.channel.members.read",
	"slack.channels.read",
	"slack.file.read",
	"slack.file.send",
	"message_channel.file.read",
	"message_channel.file.send",
	"slack.members.read",
	"slack.message.post",
	"slack.messages.search",
	"source.read",
	"source.search",
	"source.update",
	"space.agents.add",
	"space.agents.remove",
	"space.agents.wake",
	"space.context.read",
	"space.create",
	"space.files.read",
	"space.files.update",
	"space.members.add",
	"space.message.post",
	"space.settings.update",
	"space.tasks.manage",
	"spaces.read",
	"task.delegate",
	"task.watch",
	"tasks.read",
	"tests.run",
	"web.data.extract",
	"web.links.read",
	"web.search",
	"webpage.read",
	"webpage.scrape",
	"webpage.screenshot"
]);
const agentWorkSubjectKindSchema = z$1.enum([
	"agent_profile",
	"connected_app",
	"document",
	"organization",
	"project",
	"repository",
	"routine",
	"space",
	"website"
]);
const AGENT_WORK_SUBJECT_KIND_BY_KEY = {
	"agent_profile.configure": "agent_profile",
	"agent_profile.create": "agent_profile",
	"connected_app.action.execute": "connected_app",
	"connected_app.actions.read": "connected_app",
	"github.branches.read": "repository",
	"github.checks.read": "repository",
	"github.code.search": "repository",
	"github.commits.compare": "repository",
	"github.commits.read": "repository",
	"github.commits.search": "repository",
	"github.directory.read": "repository",
	"github.file.delete": "repository",
	"github.file.read": "repository",
	"github.file.update": "repository",
	"github.issue.assignees.update": "repository",
	"github.issue.comment": "repository",
	"github.issue.create": "repository",
	"github.issue.labels.update": "repository",
	"github.issue.update": "repository",
	"github.issues.read": "repository",
	"github.issues.search": "repository",
	"github.pull_request.branch.update": "repository",
	"github.pull_request.comment": "repository",
	"github.pull_request.create": "repository",
	"github.pull_request.merge": "repository",
	"github.pull_request.review": "repository",
	"github.pull_request.reviewers.update": "repository",
	"github.pull_request.update": "repository",
	"github.pull_requests.read": "repository",
	"github.ref.create": "repository",
	"github.releases.read": "repository",
	"github.repository.read": "repository",
	"github.workflow.dispatch": "repository",
	"github.workflow.rerun": "repository",
	"github.workflows.read": "repository",
	"organization.document.draft": "document",
	"project.create": "project",
	"project.update": "project",
	"repository.clone": "repository",
	"routine.create": "routine",
	"routine.update": "routine",
	"space.create": "space",
	"space.settings.update": "space",
	"web.data.extract": "website",
	"web.links.read": "website",
	"webpage.read": "website",
	"webpage.scrape": "website",
	"webpage.screenshot": "website"
};
const agentWorkSubjectSchema = z$1.strictObject({
	kind: agentWorkSubjectKindSchema,
	label: agentWorkSubjectLabelSchema
}).superRefine((subject, context) => {
	if (subject.kind === "repository" && !REPOSITORY_LABEL_PATTERN.test(subject.label)) context.addIssue({
		code: "custom",
		message: "repository subject must use owner/name",
		path: ["label"]
	});
	if (subject.kind === "website" && !(HOSTNAME_PATTERN.test(subject.label) || IPV6_HOSTNAME_PATTERN.test(subject.label))) context.addIssue({
		code: "custom",
		message: "website subject must be a hostname",
		path: ["label"]
	});
});
const agentWorkActivitySchema = z$1.strictObject({
	v: z$1.literal(1),
	key: agentWorkActivityKeySchema,
	subject: agentWorkSubjectSchema.optional(),
	outcome: z$1.literal("confirmation_required").optional()
}).superRefine((work, context) => {
	if (work.subject && AGENT_WORK_SUBJECT_KIND_BY_KEY[work.key] !== work.subject.kind) context.addIssue({
		code: "custom",
		message: "subject kind is not allowed for this work key",
		path: ["subject", "kind"]
	});
});
const agentStreamIdentityShape = {
	replyThreadRootSignalId: z$1.string().min(1).nullable().optional(),
	v: z$1.literal(1),
	spaceId: nonEmptyTrimmedStringFromUnknown,
	streamId: nonEmptyTrimmedStringFromUnknown,
	sequence: sequenceSchema,
	profileId: agentProfileIdSchema,
	triggerMessageId: nonEmptyTrimmedStringFromUnknown,
	dispatchId: nonEmptyTrimmedStringFromUnknown
};
const agentStreamPresenceShape = {
	...agentStreamIdentityShape,
	state: z$1.literal("streaming"),
	gapDetected: z$1.boolean(),
	createdAtMs: timestampSchema,
	updatedAtMs: timestampSchema,
	expiresAtMs: timestampSchema
};
function validateAgentStreamProjectionTimestamps(projection, context) {
	if (projection.updatedAtMs < projection.createdAtMs) context.addIssue({
		code: "custom",
		message: "updatedAtMs must not precede createdAtMs",
		path: ["updatedAtMs"]
	});
	if (projection.expiresAtMs < projection.updatedAtMs) context.addIssue({
		code: "custom",
		message: "expiresAtMs must not precede updatedAtMs",
		path: ["expiresAtMs"]
	});
}
const spaceAgentStreamPresenceSchema = z$1.strictObject(agentStreamPresenceShape).superRefine(validateAgentStreamProjectionTimestamps);
const spaceAgentStreamSnapshotSchema = z$1.strictObject({
	...agentStreamPresenceShape,
	textSnapshot: textSnapshotSchema
}).superRefine(validateAgentStreamProjectionTimestamps);
const spaceAgentStreamTextDeltaFrameSchema = z$1.strictObject({
	type: z$1.literal("space.agent_stream_delta"),
	...agentStreamIdentityShape,
	kind: z$1.literal("text").optional(),
	textSnapshot: textSnapshotSchema,
	state: z$1.literal("streaming"),
	updatedAtMs: timestampSchema
});
const spaceAgentStreamThinkingDeltaFrameSchema = z$1.strictObject({
	type: z$1.literal("space.agent_stream_delta"),
	...agentStreamIdentityShape,
	kind: z$1.literal("thinking"),
	thinkingSnapshot: z$1.string().max(AGENT_STREAM_SNAPSHOT_MAX_CHARS),
	reasoningState: localAgentReasoningStateSchema.optional(),
	state: z$1.literal("streaming"),
	updatedAtMs: timestampSchema
});
const spaceAgentStreamDeltaFrameSchema = z$1.union([spaceAgentStreamTextDeltaFrameSchema, spaceAgentStreamThinkingDeltaFrameSchema]);
const agentStreamToolPartSchema = z$1.strictObject({
	type: z$1.literal("tool"),
	toolCallId: toolIdentifierSchema,
	toolName: toolIdentifierSchema,
	state: z$1.enum([
		"input-available",
		"output-available",
		"output-error"
	]),
	work: agentWorkActivitySchema.optional()
});
const spaceAgentActivitySchema = z$1.strictObject({
	v: z$1.literal(1),
	steps: z$1.array(agentStreamToolPartSchema.pick({
		toolName: true,
		state: true,
		work: true
	})).min(1).max(50),
	truncated: z$1.boolean()
});
const spaceAgentStreamPartFrameSchema = z$1.strictObject({
	type: z$1.literal("space.agent_stream_part"),
	...agentStreamIdentityShape,
	part: agentStreamToolPartSchema,
	updatedAtMs: timestampSchema
});
const spaceAgentStreamTerminalSchema = z$1.union([
	z$1.strictObject({
		kind: z$1.literal("final_message"),
		publishedSignalId: nonEmptyTrimmedStringFromUnknown
	}),
	z$1.strictObject({
		kind: z$1.literal("failed"),
		reasonCode: nonEmptyTrimmedStringFromUnknown,
		noticeText: nonEmptyTrimmedStringFromUnknown
	}),
	z$1.strictObject({
		kind: z$1.literal("stopped"),
		reasonCode: nonEmptyTrimmedStringFromUnknown,
		noticeText: nonEmptyTrimmedStringFromUnknown
	}),
	z$1.strictObject({
		kind: z$1.literal("completed_but_publish_failed"),
		reasonCode: z$1.literal("remote_agent.callback_publish_failed"),
		noticeText: nonEmptyTrimmedStringFromUnknown
	})
]);
const spaceAgentStreamTerminalFrameSchema = z$1.strictObject({
	type: z$1.literal("space.agent_stream_terminal"),
	...agentStreamIdentityShape,
	terminal: spaceAgentStreamTerminalSchema,
	updatedAtMs: timestampSchema
});
//#endregion
export { AGENT_STREAM_SNAPSHOT_MAX_CHARS, agentStreamToolPartSchema, clearSpaceReasoningReplaySchema, spaceAgentActivitySchema, spaceAgentStreamDeltaFrameSchema, spaceAgentStreamPartFrameSchema, spaceAgentStreamPresenceSchema, spaceAgentStreamSnapshotSchema, spaceAgentStreamTerminalFrameSchema, spaceAgentStreamTerminalSchema };

//# sourceMappingURL=agent-stream.js.map